import React, { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useLocation, useNavigate } from 'react-router-dom';

import { Heading, PageContainer, Section } from '../../../shared/ui';
import { appendYowanPreviewQueryIfNeeded } from '../../../app/host/yowanPreviewRouting';
import { submitYowanPublicContactInquiry } from '../app/yowanContentClient';
import {
  executeYowanRecaptcha,
  getYowanRecaptchaErrorMessage,
  getYowanRecaptchaResponseErrorMessage,
  YOWAN_RECAPTCHA_ACTIONS,
} from '../app/yowanRecaptchaClient';
const { yowanPageContent } = require('./pageContent');
const { getYowanRouteMeta } = require('../routes/manifest');

const getFieldErrorMessage = (value) => {
  if (Array.isArray(value)) {
    return value.join(' ');
  }

  return typeof value === 'string' ? value : '';
};

const ContactPage = () => {
  const content = yowanPageContent['/contact'];
  const metadata = getYowanRouteMeta('/contact');
  const location = useLocation();
  const navigate = useNavigate();
  const [submitMessage, setSubmitMessage] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const {
    register,
    handleSubmit,
    reset,
    setError,
    formState: { errors },
  } = useForm({
    defaultValues: {
      applicant_type: 'inquiry',
      name: '',
      name_kana: '',
      email: '',
      phone: '',
      message: '',
      news_opt_in: true,
    },
  });

  useEffect(() => {
    if (!metadata) {
      return;
    }

    document.title = metadata.title;

    if (metadata.description) {
      const descriptionTag = document.querySelector('meta[name="description"]');
      if (descriptionTag) {
        descriptionTag.setAttribute('content', metadata.description);
      }
    }
  }, [metadata]);

  const onSubmit = async (values) => {
    setIsSubmitting(true);
    setSubmitMessage(content.form.pendingMessage);

    try {
      const recaptchaToken = await executeYowanRecaptcha(
        YOWAN_RECAPTCHA_ACTIONS.contactInquiry,
      );

      await submitYowanPublicContactInquiry({
        ...values,
        recaptcha_token: recaptchaToken,
      });
      reset();
      navigate(appendYowanPreviewQueryIfNeeded('/', location.search), {
        state: { notice: content.form.successMessage },
      });
    } catch (error) {
      const localRecaptchaMessage = getYowanRecaptchaErrorMessage(error?.code);

      if (localRecaptchaMessage) {
        setSubmitMessage(localRecaptchaMessage);
        return;
      }

      const responseData = error?.response?.data || {};
      const fieldNames = ['name', 'name_kana', 'email', 'phone', 'message'];

      fieldNames.forEach((fieldName) => {
        const message = getFieldErrorMessage(responseData[fieldName]);

        if (message) {
          setError(fieldName, { type: 'server', message });
        }
      });

      setSubmitMessage(
        getYowanRecaptchaResponseErrorMessage(responseData) ||
          responseData.detail ||
          content.form.errorMessage,
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const hasGuidance =
    content.guidance.paragraphs.length > 0 || content.guidance.channels.length > 0;

  return (
    <>
      <Section>
        <PageContainer>
          <div className="yowan-contact-header">
            <div className="yowan-contact-header__body">
              <Heading as="h1">{content.header.title}</Heading>
              {content.header.paragraphs.map((paragraph) => (
                <p key={paragraph}>{paragraph}</p>
              ))}
            </div>
          </div>
        </PageContainer>
      </Section>
      <Section>
        <PageContainer>
          <div className={`yowan-contact-layout${hasGuidance ? '' : ' yowan-contact-layout--single'}`}>
            {hasGuidance ? (
              <div className="yowan-contact-guidance c-surface-content">
                {content.guidance.paragraphs.length ? (
                  <div className="yowan-contact-guidance__body">
                    {content.guidance.paragraphs.map((paragraph) => (
                      <p key={paragraph}>{paragraph}</p>
                    ))}
                  </div>
                ) : null}
                {content.guidance.channels.length ? (
                  <dl className="yowan-contact-guidance__channels">
                    {content.guidance.channels.map((channel) => (
                      <div key={channel.label} className="yowan-contact-guidance__channel">
                        <dt>{channel.label}</dt>
                        <dd>
                          {channel.href && channel.external ? (
                            <a href={channel.href} target="_blank" rel="noreferrer">
                              {channel.value}
                            </a>
                          ) : channel.href ? (
                            <a href={channel.href}>{channel.value}</a>
                          ) : (
                            channel.value
                          )}
                        </dd>
                      </div>
                    ))}
                  </dl>
                ) : null}
              </div>
            ) : null}
            <form className="yowan-contact-form c-surface-content c-form" onSubmit={handleSubmit(onSubmit)}>
              {content.form.lead ? (
                <p className="yowan-contact-form__lead">{content.form.lead}</p>
              ) : null}
              <input type="hidden" value="inquiry" {...register('applicant_type')} />

              <label className="yowan-contact-form__field c-form__field">
                <span className="c-form__label">
                  {content.form.labels.name}
                  <em className="c-form__required">*</em>
                </span>
                <input
                  className="c-form__control"
                  type="text"
                  {...register('name', { required: content.form.errors.required })}
                />
                {errors.name ? (
                  <em className="yowan-contact-form__error">{errors.name.message}</em>
                ) : null}
              </label>

              <label className="yowan-contact-form__field c-form__field">
                <span className="c-form__label">{content.form.labels.nameKana}</span>
                <input className="c-form__control" type="text" {...register('name_kana')} />
              </label>

              <label className="yowan-contact-form__field c-form__field">
                <span className="c-form__label">
                  {content.form.labels.email}
                  <em className="c-form__required">*</em>
                </span>
                <input
                  className="c-form__control"
                  type="email"
                  {...register('email', { required: content.form.errors.required })}
                />
                {errors.email ? (
                  <em className="yowan-contact-form__error">{errors.email.message}</em>
                ) : null}
              </label>

              <label className="yowan-contact-form__field c-form__field">
                <span className="c-form__label">{content.form.labels.phone}</span>
                <input className="c-form__control" type="tel" {...register('phone')} />
              </label>

              <label className="yowan-contact-form__field c-form__field">
                <span className="c-form__label">
                  {content.form.labels.message}
                  <em className="c-form__required">*</em>
                </span>
                <textarea
                  className="c-form__control c-form__control--textarea"
                  rows="8"
                  {...register('message', { required: content.form.errors.required })}
                />
                {errors.message ? (
                  <em className="yowan-contact-form__error">{errors.message.message}</em>
                ) : null}
              </label>

              <div className="yowan-contact-form__field yowan-contact-form__field--subscription c-form__field c-form__field--choice">
                <span className="c-form__label">{content.form.labels.newsOptIn}</span>
                <div className="c-form__choices yowan-contact-form__subscription-choice">
                  <label className="c-form__choice">
                    <input
                      type="checkbox"
                      {...register('news_opt_in')}
                    />
                    <span>{content.form.newsOptInLabel}</span>
                  </label>
                </div>
                <p className="yowan-contact-form__help">{content.form.newsOptInHelp}</p>
              </div>

              <button
                type="submit"
                className="yowan-contact-form__submit c-form__submit"
                disabled={isSubmitting}
              >
                {isSubmitting ? content.form.pendingMessage : content.form.submitLabel}
              </button>
              {submitMessage ? (
                <p className="yowan-contact-form__message">{submitMessage}</p>
              ) : null}
            </form>
          </div>
        </PageContainer>
      </Section>
    </>
  );
};

export default ContactPage;
